import { useState } from 'react';

interface ResultsPanelProps {
  ping: number;
  jitter: number;
  loss: number;
  download: number;
  upload: number;
  bloat: number;
  dns: number;
  bestStream: string;
  timestamp: Date;
  visible: boolean;
}

function getGrade(ping: number, dl: number, ul: number, jitter: number, loss: number, bloat: number) {
  let score = 0;
  if (ping < 10) score += 25;
  else if (ping < 30) score += 20;
  else if (ping < 60) score += 14;
  else if (ping < 100) score += 8;
  else score += 3;

  if (dl > 500) score += 30;
  else if (dl > 100) score += 25;
  else if (dl > 50) score += 19;
  else if (dl > 25) score += 13;
  else if (dl > 10) score += 7;
  else score += 2;

  if (ul > 200) score += 25;
  else if (ul > 50) score += 20;
  else if (ul > 20) score += 14;
  else if (ul > 10) score += 8;
  else score += 2;

  if (jitter < 5) score += 10;
  else if (jitter < 20) score += 7;
  else if (jitter < 50) score += 4;

  if (loss < 0.5) score += 5;
  else if (loss < 2) score += 3;

  if (bloat < 20) score += 5;
  else if (bloat < 50) score += 2;

  const grades = [
    { min: 95, g: 'S+', c: 'hsl(var(--st-green))', d: 'Exceptional' },
    { min: 85, g: 'S', c: 'hsl(var(--st-green))', d: 'Outstanding' },
    { min: 75, g: 'A+', c: 'hsl(120, 100%, 50%)', d: 'Excellent' },
    { min: 65, g: 'A', c: 'hsl(120, 100%, 50%)', d: 'Very Good' },
    { min: 55, g: 'B+', c: 'hsl(var(--st-amber))', d: 'Good' },
    { min: 45, g: 'B', c: 'hsl(var(--st-amber))', d: 'Above Average' },
    { min: 35, g: 'C', c: 'hsl(30, 100%, 50%)', d: 'Average' },
    { min: 0, g: 'D', c: 'hsl(var(--st-pink))', d: 'Poor' },
  ];

  return grades.find(x => score >= x.min) || grades[grades.length - 1];
}

export function ResultsPanel({
  ping,
  jitter,
  loss,
  download,
  upload,
  bloat,
  dns,
  bestStream,
  timestamp,
  visible,
}: ResultsPanelProps) {
  const [copied, setCopied] = useState(false);
  const grade = getGrade(ping, download, upload, jitter, loss, bloat);

  const shareResults = () => {
    const txt = `⚡ SpeedTest Pro Results\nPing: ${ping.toFixed(1)} ms | Jitter: ${jitter.toFixed(1)} ms\nDownload: ${download.toFixed(2)} Mbps | Upload: ${upload.toFixed(2)} Mbps\nPacket Loss: ${loss.toFixed(1)}% | Bloat: ${bloat.toFixed(0)} ms\nDNS: ${dns.toFixed(0)} ms | Best Stream: ${bestStream}\n${timestamp.toLocaleString()}`;

    navigator.clipboard.writeText(txt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className={`bg-[hsl(var(--st-surface-1))] border border-[hsl(var(--st-border))] rounded-xl p-4 flex flex-col gap-3 transition-all ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
      }`}
    >
      <div className="flex items-center gap-4">
        <div
          className="text-5xl font-bold"
          style={{ color: grade.c, textShadow: `0 0 24px ${grade.c}66` }}
        >
          {grade.g}
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-xs text-[hsl(var(--st-dim))] uppercase tracking-widest">
            Connection Grade
          </div>
          <div className="text-sm text-[hsl(var(--st-text))]">{grade.d}</div>
        </div>
      </div>

      <div className="space-y-px border-t border-[hsl(var(--st-border))]">
        {[
          ['PING', `${ping.toFixed(1)} ms`],
          ['JITTER', `${jitter.toFixed(1)} ms`],
          ['PACKET LOSS', `${loss.toFixed(1)}%`],
          ['DOWNLOAD', `${download.toFixed(2)} Mbps`],
          ['UPLOAD', `${upload.toFixed(2)} Mbps`],
          ['BUFFER BLOAT', `${bloat.toFixed(0)} ms`],
          ['DNS', `${dns.toFixed(0)} ms`],
          ['MAX STREAM', bestStream],
          ['TESTED', timestamp.toLocaleString()],
        ].map(([k, v]) => (
          <div key={k} className="flex justify-between py-2 px-0 text-xs border-b border-[hsl(var(--st-border))]">
            <span className="text-[hsl(var(--st-dim))] uppercase tracking-wider">{k}</span>
            <span className="font-semibold text-white">{v}</span>
          </div>
        ))}
      </div>

      <button
        onClick={shareResults}
        className="text-xs uppercase tracking-wider border border-[hsl(var(--st-dim))] text-[hsl(var(--st-dim))] py-2 rounded transition-all hover:border-[hsl(var(--st-cyan))] hover:text-[hsl(var(--st-cyan))]"
      >
        {copied ? '✓ Copied!' : '⊡ Copy Results'}
      </button>
    </div>
  );
}
